import ButtonV2 from 'components/common/ButtonV2';
import { FSBTable } from 'components/common/FSBTable';
import React, { useContext, useEffect } from 'react';
import {
  convertEpochToDate,
  dateToMilliseconds,
  paymentDetailsColumns,
} from 'pages/Orders/constant';
import { BsEyeFill } from 'react-icons/bs';
import { Button, Card, CardHeader } from 'reactstrap';
import { axiosGetAPI, axiosPost, axiosUpdate } from 'services';
import { toast } from 'react-toastify';
import { authContext } from 'contexts/AuthContext';
import { currencyToLocal } from 'utils/constants';

import PaymentForm from './PaymentForm';

const BuyerPaymentSection = ({
  values,
  setFieldValue,
  handleShowProof,
  grandTotal,
  refetchOrderData,
  onOrderUpdated,
}) => {
  const { auth } = useContext(authContext);

  const [showPaymentForm, setShowPaymentForm] = React.useState(false);
  const [isEdit, setIsEdit] = React.useState(false);
  const [isProofVerifyProcessing, setIsProofVerifyProcessing] =
    React.useState(false);
  const [orderPaymentList, setOrderPaymentList] = React.useState([]);

  const fetchOrderPayments = async (orderId) => {
    try {
      const resp = await axiosGetAPI(
        `${process.env.REACT_APP_REST_API_ENDPOINT}order/get-payment-details/${orderId}`,
        auth,
      );
      if (resp?.isSuccessful) {
        const buyerPayments =
          resp?.data?.data?.filter((payment) => payment?.type === 'buyer') || [];
        setOrderPaymentList((prev) => {
          if (JSON.stringify(prev) === JSON.stringify(buyerPayments)) {
            return prev;
          }
          return buyerPayments;
        });
      }
    } catch (error) {
      console.error('Error fetching Order Payments:', error);
    }
  };

  const handlePaymentStatus = async (payload) => {
    try {
      setIsProofVerifyProcessing(
        `${payload?.paymentId}-${payload?.paymentProofStatus}`,
      );
      const resp = await axiosPost(
        `${process.env.REACT_APP_REST_API_ENDPOINT}order/update-payment-status`,
        { ...payload },
        {},
        auth,
      );
      if (resp?.isSuccessful) {
        toast.success('Payment status updated successfully!');
      }
    } catch (error) {
      console.error('Error while updating payment status:', error);
    } finally {
      setIsProofVerifyProcessing(null);
      fetchOrderPayments(values?.id);
      if (refetchOrderData) refetchOrderData();
      if (onOrderUpdated) onOrderUpdated();
    }
  };

  const onCreatePayment = async () => {
    try {
      const buyerPayment = values?.paymentDetails?.buyer;
      const utr =
        buyerPayment?.utrnumber ||
        buyerPayment?.UTRNumber ||
        buyerPayment?.utrNumber ||
        '';
      const proof = Array?.isArray(buyerPayment?.proofUrl)
        ? buyerPayment?.proofUrl[0]
        : buyerPayment?.proofUrl || null;

      const payload = {
        orderId: Number(values?.id) || values?.id,
        UTRNumber: String(utr).trim(),
        paidDate: dateToMilliseconds(buyerPayment?.paidDate),
        paidAmount: Number(buyerPayment?.paidAmount),
        proofUrl: proof || null,
        type: 'buyer',
      };

      const resp = await axiosPost(
        `${process.env.REACT_APP_REST_API_ENDPOINT}order/add-payment-details-to-order`,
        payload,
        {},
        auth,
      );
      if (resp?.isSuccessful) {
        toast.success(
          resp?.data?.message || 'Payment detail added successfully',
        );
      } else if (
        !resp?.isSuccessful &&
        (resp?.data?.message || resp?.message)
      ) {
        toast.error(resp?.data?.message || resp?.message);
      }
    } catch (error) {
      console.error('Error while creating payment:', error);
    } finally {
      fetchOrderPayments(values?.id);
      if (refetchOrderData) refetchOrderData();
      if (onOrderUpdated) onOrderUpdated();
    }
  };

  const onUpdatePayment = async () => {
    try {
      const buyerPayment = values?.paymentDetails?.buyer;
      const utr =
        buyerPayment?.utrnumber ||
        buyerPayment?.UTRNumber ||
        buyerPayment?.utrNumber ||
        '';
      const proof = Array?.isArray(buyerPayment?.proofUrl)
        ? buyerPayment?.proofUrl[0]
        : buyerPayment?.proofUrl || null;

      const payload = {
        paymentId: buyerPayment?.paymentId,
        orderId: Number(values?.id) || values?.id,
        UTRNumber: String(utr).trim(),
        paidDate: dateToMilliseconds(buyerPayment?.paidDate),
        paidAmount: Number(buyerPayment?.paidAmount),
        proofUrl: proof,
        type: 'buyer',
      };

      const resp = await axiosUpdate(
        `${process.env.REACT_APP_REST_API_ENDPOINT}order/update-payment-details-to-order`,
        payload,
        auth?.data?.token,
      );
      if (resp?.isSuccessful) {
        toast.success(
          resp?.data?.message || 'Payment details updated successfully!',
        );
      } else if (
        !resp?.isSuccessful &&
        (resp?.data?.message || resp?.message)
      ) {
        toast.error(resp?.data?.message || resp?.message);
      }
    } catch (error) {
      console.error('Error while adding payments');
    } finally {
      fetchOrderPayments(values?.id);
      if (refetchOrderData) refetchOrderData();
      if (onOrderUpdated) onOrderUpdated();
    }
  };

  useEffect(() => {
    if (!values?.id) return undefined;

    fetchOrderPayments(values?.id);

    const intervalId = setInterval(() => {
      fetchOrderPayments(values?.id);
    }, 3000);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values?.id, auth]);

  const plusIconSvg = (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M5.00146 1.24976C5.08435 1.24976 5.16454 1.28295 5.22314 1.34155C5.28163 1.40004 5.31384 1.47955 5.31396 1.56226V4.68726H8.43896C8.52184 4.68726 8.60204 4.72045 8.66064 4.77905C8.71913 4.83754 8.75134 4.91705 8.75146 4.99976C8.75146 5.08264 8.71925 5.16283 8.66064 5.22144C8.60204 5.28004 8.52184 5.31226 8.43896 5.31226H5.31396V8.43726C5.31396 8.52014 5.28175 8.60033 5.22314 8.65894C5.16454 8.71754 5.08435 8.74976 5.00146 8.74976C4.91876 8.74963 4.83925 8.71742 4.78076 8.65894C4.72216 8.60033 4.68896 8.52014 4.68896 8.43726V5.31226H1.56396C1.48126 5.31213 1.40175 5.27992 1.34326 5.22144C1.28466 5.16283 1.25146 5.08264 1.25146 4.99976C1.25159 4.91705 1.28477 4.83755 1.34326 4.77905C1.40175 4.72056 1.48126 4.68738 1.56396 4.68726H4.68896V1.56226C4.68909 1.47955 4.72227 1.40005 4.78076 1.34155C4.83925 1.28306 4.91876 1.24988 5.00146 1.24976Z"
        fill="white"
      />
    </svg>
  );

  const findIndexOfItem = (item) => {
    const findIndex = paymentDetailsColumns.findIndex(
      (itm) => itm?.accessorKey === item,
    );
    return findIndex;
  };

  const editIconSVG = (
    <svg
      width="10"
      height="10"
      viewBox="0 0 10 10"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M1.45543 6.23022C1.49027 6.24329 1.52759 6.24969 1.56481 6.24976H1.87731V6.56226C1.87731 6.64514 1.90952 6.72533 1.96813 6.78394C2.02673 6.84251 2.10695 6.87476 2.18981 6.87476H2.50231V7.18726C2.50231 7.27014 2.53452 7.35033 2.59313 7.40894C2.65173 7.46751 2.73195 7.49976 2.81481 7.49976H3.12731V7.81226C3.12731 7.89514 3.15952 7.97533 3.21813 8.03394C3.27673 8.09251 3.35695 8.12476 3.43981 8.12476H3.75231V8.43726C3.75238 8.47451 3.75876 8.51175 3.77184 8.54663V8.54761L3.66051 8.65894C3.63065 8.68855 3.59509 8.71173 3.55602 8.72729L0.431017 9.97729C0.374227 10 0.311252 10.0062 0.251329 9.9939C0.191522 9.98157 0.136309 9.95114 0.0931263 9.90796C0.0502096 9.86487 0.0204664 9.81029 0.00816534 9.75073C-0.00412682 9.69081 0.00203524 9.62784 0.0247669 9.57104L1.27477 6.44604C1.29033 6.40696 1.31349 6.37142 1.34313 6.34155L1.45543 6.23022ZM8.43981 3.87964L4.37731 7.94214V7.81226C4.37722 7.72952 4.34401 7.65008 4.28551 7.59155C4.227 7.53305 4.14753 7.49987 4.06481 7.49976H3.75231V7.18726C3.75222 7.10452 3.71901 7.02508 3.66051 6.96655C3.602 6.90805 3.52253 6.87487 3.43981 6.87476H3.12731V6.56226C3.12722 6.47952 3.09401 6.40008 3.03551 6.34155C2.977 6.28305 2.89753 6.24987 2.81481 6.24976H2.50231V5.93726C2.50222 5.85452 2.46901 5.77508 2.41051 5.71655C2.352 5.65805 2.27253 5.62487 2.18981 5.62476H2.05992L6.12242 1.56226L8.43981 3.87964ZM7.81481 -0.000244141C7.89763 -0.000213759 7.97693 0.0329951 8.03551 0.0915527L9.91051 1.96655C9.93959 1.99557 9.96312 2.03017 9.97887 2.06812C9.99448 2.10588 10.0023 2.14639 10.0023 2.18726C10.0023 2.22833 9.9946 2.26943 9.97887 2.30737C9.96311 2.34534 9.93961 2.37991 9.91051 2.40894L8.88121 3.43726L6.56481 1.12085L7.5941 0.0915527C7.6527 0.0330091 7.73197 -0.000244141 7.81481 -0.000244141Z"
        fill="white"
      />
    </svg>
  );

  const actionsFormatter = (cell) => {
    const { row } = cell;
    const EditButton = (
      <ButtonV2
        style={{ fontWeight: '400' }}
        className="border-radius-4"
        color="primary"
        key="edit"
        onClick={() => {
          setFieldValue('paymentDetails.buyer', {
            ...values?.paymentDetails,
            proofUrl: row?.original?.proofUrl
              ? [row?.original?.proofUrl]
              : null,
            paymentId: row?.original?.paymentCheckoutId,
            paidDate: convertEpochToDate(row?.original?.paidDate),
            paidAmount: row?.original?.paidAmount,
            utrnumber: row?.original?.utrNumber,
          });
          setIsEdit(true);
          setShowPaymentForm(true);
        }}
      >
        <p style={{ textWrap: 'nowrap' }}>
          {editIconSVG}
          <span style={{ paddingLeft: '4px' }}>Edit</span>
        </p>
      </ButtonV2>
    );
    const isVerified = row?.original?.isProofVerified;

    if (isVerified === null || isVerified === undefined) {
      return (
        <div className="d-flex gap-2">
          {EditButton}
          <ButtonV2
            size="sm"
            className="rounded-1"
            color="success"
            type="button"
            outline
            onClick={() => {
              handlePaymentStatus({
                orderId: values?.id,
                paymentId: row?.original?.paymentCheckoutId,
                paymentProofStatus: true,
              });
            }}
          >
            {isProofVerifyProcessing ===
            `${row?.original?.paymentCheckoutId}-true`
              ? 'Processing...'
              : 'Accept'}
          </ButtonV2>
          <ButtonV2
            color="danger"
            type="button"
            className="py-1 rounded-0 f-14 ms-2 border-radius-4"
            onClick={() => {
              handlePaymentStatus({
                orderId: values?.id,
                paymentId: row?.original?.paymentCheckoutId,
                paymentProofStatus: false,
              });
            }}
            outline
          >
            {isProofVerifyProcessing ===
            `${row?.original?.paymentCheckoutId}-false`
              ? 'Rejecting...'
              : 'Reject'}
          </ButtonV2>
        </div>
      );
    }
    if (isVerified === true) {
      return (
        <div className="d-flex gap-2 justify-content-center">
          {row?.original?.isAdminCreated && EditButton}
          <div className="accepted-badge">Accepted</div>
        </div>
      );
    }
    return (
      <div className="d-flex justify-content-center">
        <div className="rejected-badge">Rejected</div>
      </div>
    );
  };

  const recieptFormatter = (cell) => {
    const { row } = cell;
    return (
      <div className="d-flex justify-content-center">
        <Button
          color="link"
          size="sm"
          className="text-decoration-none d-flex gap-2 align-items-center"
          onClick={() => {
            handleShowProof(
              row?.original?.preSignedProofUrl,
              row?.original?.utrNumber,
            );
          }}
          disabled={!row?.original?.proofUrl}
        >
          <BsEyeFill />
          View Proof
        </Button>
      </div>
    );
  };

  paymentDetailsColumns[findIndexOfItem('reciept')].cell = recieptFormatter;
  paymentDetailsColumns[findIndexOfItem('actions')].cell = actionsFormatter;

  const remainingBalance = Number(
    (
      (grandTotal ?? values?.total ?? 0) -
      (orderPaymentList?.reduce(
        (a, c) =>
          a +
          ((c?.isProofVerified || c?.isProofVerified === null
            ? c?.paidAmount
            : 0) || 0),
        0,
      ) || 0)
    ).toFixed(2),
  );

  const format = (value) => {
    return currencyToLocal(
      value,
      values?.rateUnit?.isoCurrencyCode || values?.rateUnit?.label || 'INR',
    );
  };

  return (
    <Card
      className="table-card"
      style={{ border: '1px solid #EBEBEB', overflow: 'hidden' }}
    >
      <CardHeader
        className="order-payment__table-header"
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '10px 12px',
        }}
      >
        <div className="f-16 fw-semibold" style={{ marginBottom: 0 }}>
          Buyer&apos;s Payment Details
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <p key="info" style={{ color: '#BB3232' }}>
            Total: <b>{format(grandTotal ?? values?.total ?? 0)}</b>{' '}
            Pending:{' '}
            <b>{format(remainingBalance)}</b>
          </p>
          <ButtonV2
            style={{ fontWeight: '400' }}
            size="sm"
            className="border-radius-4"
            color="primary"
            key="edit"
            onClick={() => {
              setShowPaymentForm(true);
              setIsEdit(false);
            }}
          >
            {plusIconSvg}
            <span style={{ paddingLeft: '6px' }}>Add</span>
          </ButtonV2>
        </div>
      </CardHeader>
      <FSBTable
        columns={paymentDetailsColumns}
        data={orderPaymentList}
        totalCount={0}
        totalPages={0}
        currentPageNo={0}
        pageSize={10}
        keyField="id"
        showSearch={false}
        showFilters
        showDelete={false}
        showPagination
        remote
        tableClass="action-table order-table"
        striped={false}
        hover={false}
        bordered={false}
        wrapperClass="table-responsive word-break "
        handlePageChange={() => {
          // No need to change page
          // handlePageProp not nullable
        }}
        globalSearchStr=""
        onGlobalSearch={() => {
          // No need for global search
          // onGlobalSearch not nullable
        }}
        onSortChange={() => {
          // No need to sort change
          // onSortChange not nullable
        }}
        sorting={undefined}
        searchPlaceholder="Search Orders"
      >
        {/* Only can see add and delete buttons as Only admin can add or delete users */}
      </FSBTable>

      {showPaymentForm && (
        <div style={{ marginTop: '20px' }}>
          <PaymentForm
            isEdit={isEdit}
            values={values}
            setFieldValue={setFieldValue}
            setShowForm={setShowPaymentForm}
            onCreatePayment={onCreatePayment}
            onUpdatePayment={onUpdatePayment}
          />
        </div>
      )}
    </Card>
  );
};

export default BuyerPaymentSection;
